import { format, parseISO, subDays } from 'date-fns'
import { ChevronDown, Landmark } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { useKMH, useKMHAccrue, type KMHAccount, type KMHAccrual } from '../../kmh'
import { toastError } from '../../ui'

/** KMH (ek hesap) kullanımı: limit, nakit avans, eksi bakiye, işleyen faiz ve geçmiş tahakkuklar. */
export function KMHSection({ canAccrue }: { canAccrue: boolean }) {
  const { t } = useTranslation()
  const q = useKMH()
  return (
    <Section title={t('finance.kmh.title')} icon={Landmark} bodyClassName="p-0">
      <p className="text-muted-foreground border-b px-4 py-2 text-xs">{t('finance.kmh.hint')}</p>
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.isLoading ? (
        <Skeleton className="m-4 h-16" />
      ) : !q.data?.length ? (
        <EmptyState icon={Landmark} title={t('finance.cashAdvances.noLimitAccounts')} />
      ) : (
        <ul className="divide-y">
          {q.data.map((a) => (
            <AccountRow key={a.accountId} a={a} canAccrue={canAccrue} />
          ))}
        </ul>
      )}
    </Section>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'danger' | 'success' }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className={cn('font-semibold tabular-nums', tone === 'danger' && 'text-destructive', tone === 'success' && 'text-success')}>
        {fmtMoney(value)}
      </p>
    </div>
  )
}

function AccountRow({ a, canAccrue }: { a: KMHAccount; canAccrue: boolean }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const accrue = useKMHAccrue()
  // İşleyen dönem bugüne kadar; elle tahakkuk düne kadar yapılır (bugün henüz bitmedi).
  const yesterday = a.running ? format(subDays(parseISO(a.running.periodTo), 1), 'yyyy-MM-dd') : ''
  const canRun = canAccrue && !!a.running && a.running.periodFrom <= yesterday
  return (
    <li className="space-y-3 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="min-w-0 flex-1 basis-48">
          <p className="font-medium">{a.name}</p>
          {a.bankName ? <p className="text-muted-foreground text-xs">{a.bankName}</p> : null}
        </div>
        <div className="grid flex-[2] grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label={t('finance.kmh.limit')} value={a.limit} />
          <Stat label={t('finance.kmh.cashAdvance')} value={a.cashAdvance} />
          <Stat label={t('finance.kmh.used')} value={a.kmhUsed} tone={Number(a.kmhUsed) > 0 ? 'danger' : undefined} />
          <Stat label={t('finance.kmh.available')} value={a.available} tone="success" />
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {t('finance.kmh.details')}
          <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
        </Button>
      </div>
      {a.running && Number(a.running.total) > 0 ? (
        <p className="bg-muted/50 rounded-lg px-3 py-2 text-xs">
          {t('finance.kmh.running', {
            from: fmtDate(a.running.periodFrom),
            to: fmtDate(a.running.periodTo),
            interest: fmtMoney(a.running.interest),
            total: fmtMoney(a.running.total),
            rate: a.running.rate,
          })}
        </p>
      ) : null}
      {open ? (
        <div className="space-y-3">
          {a.running?.lines.length ? <Lines title={t('finance.kmh.runningLines')} accrual={a.running} /> : null}
          {a.accruals.length ? (
            a.accruals.map((x) => (
              <Lines
                key={x.id}
                title={t('finance.kmh.accrual', {
                  from: fmtDate(x.periodFrom),
                  to: fmtDate(x.periodTo),
                  total: fmtMoney(x.total),
                })}
                accrual={x}
              />
            ))
          ) : (
            <p className="text-muted-foreground text-xs">{t('finance.kmh.noAccruals')}</p>
          )}
          {canRun ? (
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={accrue.isPending}
                onClick={() =>
                  void confirmModal({
                    title: t('finance.kmh.accrueTitle'),
                    description: t('finance.kmh.accrueBody', { from: fmtDate(a.running!.periodFrom), to: fmtDate(yesterday) }),
                    confirmLabel: t('finance.kmh.accrue'),
                  }).then(
                    (ok) =>
                      ok &&
                      accrue.mutate(
                        { accountId: a.accountId, until: yesterday },
                        { onSuccess: () => toast.success(t('finance.kmh.accrued')), onError: toastError },
                      ),
                  )
                }
              >
                {t('finance.kmh.accrue')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

function Lines({ title, accrual }: { title: string; accrual: KMHAccrual }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border">
      <p className="flex flex-wrap justify-between gap-2 border-b px-3 py-2 text-xs font-medium">
        <span>{title}</span>
        <span className="text-muted-foreground font-normal">
          {t('finance.kmh.taxes', { rate: accrual.rate, bsmv: fmtMoney(accrual.bsmv), kkdf: fmtMoney(accrual.kkdf) })}
        </span>
      </p>
      {accrual.lines.length ? (
        <ul className="divide-y">
          {accrual.lines.map((l) => (
            <li key={l.from} className="flex flex-wrap items-center gap-x-3 px-3 py-1.5 text-xs">
              <span className="text-muted-foreground min-w-0 flex-1">
                {t('finance.kmh.line', { from: fmtDate(l.from), to: fmtDate(l.to), days: l.days, used: fmtMoney(l.used) })}
              </span>
              <span className="tabular-nums">{fmtMoney(l.amount)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground px-3 py-2 text-xs">{t('finance.kmh.noUsage')}</p>
      )}
    </div>
  )
}
